import { useRef } from "react";
import InteractiveElementInlineCard from "@/components/interactive/InteractiveElementInlineCard";
import QuizCodeAssertion from "@/components/storyboard/quiz/QuizCodeAssertion";
import QuizCodeDebug from "@/components/storyboard/quiz/QuizCodeDebug";
import QuizCodeExplanation from "@/components/storyboard/quiz/QuizCodeExplanation";
import QuizCodeFreetext from "@/components/storyboard/quiz/QuizCodeFreetext";
import QuizCodeTrueFalse, {
  type QuizCodeTrueFalseState,
} from "@/components/storyboard/quiz/QuizCodeTrueFalse";
import QuizFreetext from "@/components/storyboard/quiz/QuizFreetext";
import QuizGapwordDropdown from "@/components/storyboard/quiz/QuizGapwordDropdown";
import QuizGapwordFreeText from "@/components/storyboard/quiz/QuizGapwordFreeText";
import QuizMatching from "@/components/storyboard/quiz/QuizMatching";
import QuizMultipleChoice, {
  type QuizMultipleChoiceState,
} from "@/components/storyboard/quiz/QuizMultipleChoice";
import QuizTrueFalse, {
  type QuizTrueFalseState,
} from "@/components/storyboard/quiz/QuizTrueFalse";
import { isWideQuiz } from "@/lib/codeQuiz";
import { encodeClozeFreetextAnswers } from "@/lib/freetextQuizStatus";
import {
  illustrationKindFromSubType,
  INTERACTIVE_ILLUSTRATION_BY_SUBTYPE,
  type InteractiveIllustrationKind,
  type InteractiveIllustrationSubType,
} from "@/lib/interactiveIllustration";
import {
  decodeAnswer,
  validationType,
} from "@/lib/courseCompletionQuizAnswers";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { CompletionQuizQuestionContent } from "@/types/courseCompletionQuiz";

interface QuizRunQuestionProps {
  content: CompletionQuizQuestionContent;
  /** A previous attempt at this question, shown again when grading failed. */
  savedInput?: string | null;
  /** „Nächste Frage“ or „Zur Auswertung“. Saving happens when it is clicked. */
  advanceLabel: string;
  onSubmit: (type: string, userInput: string) => Promise<void>;
  onAdvance: () => void;
}

const BLANK_FEEDBACK = { positive: "", negative: "" };

const storedWithoutVerdict = (): ValidationResult => ({
  correct: null,
  feedback: "",
});

const illustrationFor = (subType: string): InteractiveIllustrationKind => {
  if (subType in INTERACTIVE_ILLUSTRATION_BY_SUBTYPE) {
    return illustrationKindFromSubType(
      subType as InteractiveIllustrationSubType
    );
  }
  return "multiple_choice";
};

const cardTitle = (
  content: CompletionQuizQuestionContent
): string | undefined =>
  content.instruction ||
  content.challenge ||
  content.codeChallenge ||
  content.debugChallenge ||
  undefined;

const trueFalsePartial = (
  content: CompletionQuizQuestionContent,
  savedInput?: string | null
): QuizTrueFalseState | undefined => {
  if (!savedInput) return undefined;
  const draft = decodeAnswer(content, savedInput);
  if (draft.kind !== "boolean" || draft.value === null) return undefined;
  return {
    currentIndex: 0,
    selectedAnswer: draft.value,
    isSubmitted: false,
    isCorrect: null,
    correctCount: 0,
  };
};

const choicePartial = (
  content: CompletionQuizQuestionContent,
  savedInput?: string | null
): QuizMultipleChoiceState | undefined => {
  if (!savedInput) return undefined;
  const draft = decodeAnswer(content, savedInput);
  if (draft.kind !== "choice") return undefined;
  return {
    currentIndex: 0,
    selectedLabels: draft.selected,
    isSubmitted: false,
    isCorrect: null,
    correctCount: 0,
  };
};

/**
 * One question of the course completion quiz. Card questions use the same
 * inline card as in the course. Code workspace questions fill the page
 * instead. The answer is stored when the learner moves on. Whether it was
 * right stays hidden.
 */
const QuizRunQuestion = ({
  content,
  savedInput,
  advanceLabel,
  onSubmit,
  onAdvance,
}: QuizRunQuestionProps) => {
  const saving = useRef(false);
  const save = async (userInput: string) => {
    if (saving.current) throw new Error("busy");
    saving.current = true;
    try {
      await onSubmit(validationType(content.subType), userInput);
    } finally {
      saving.current = false;
    }
  };

  const collect = {
    revealFeedback: false as const,
    advanceLabel,
    onAdvance,
  };

  const renderQuestion = () => {
    switch (content.subType) {
      case "MULTIPLE_CHOICE":
      case "CODE_MULTIPLE_CHOICE":
        return (
          <QuizMultipleChoice
            {...collect}
            variant={
              content.subType === "CODE_MULTIPLE_CHOICE" ? "code" : "default"
            }
            entries={[
              {
                answers: content.answers ?? [],
                question: content.question ?? "",
                subType: content.subType,
                contentType: "QUIZ",
                instruction: content.instruction ?? "",
                userFeedback: BLANK_FEEDBACK,
                selectCount: content.selectCount ?? 1,
              },
            ]}
            initialPartial={choicePartial(content, savedInput)}
            onValidate={async (selected) => {
              await save(JSON.stringify(selected));
              return storedWithoutVerdict();
            }}
          />
        );
      case "TRUE_FALSE":
        return (
          <QuizTrueFalse
            {...collect}
            entries={[
              {
                statement: content.statement ?? "",
                instruction: content.instruction ?? "",
                userFeedback: BLANK_FEEDBACK,
              },
            ]}
            initialPartial={trueFalsePartial(content, savedInput)}
            onValidate={async (answer) => {
              await save(String(answer));
              return false;
            }}
          />
        );
      case "CODE_TRUE_FALSE":
        return (
          <QuizCodeTrueFalse
            {...collect}
            entries={[
              {
                statement: content.statement ?? "",
                codeSnippet: content.codeSnippet,
                instruction: content.instruction ?? "",
                userFeedback: BLANK_FEEDBACK,
                correctAnswer: false,
              },
            ]}
            initialPartial={
              trueFalsePartial(content, savedInput) as
                | QuizCodeTrueFalseState
                | undefined
            }
            onValidate={async (answer) => {
              await save(String(answer));
              return false;
            }}
          />
        );
      case "CLOZE_DROPDOWN":
        return (
          <QuizGapwordDropdown
            {...collect}
            entries={[
              {
                clozeText: content.clozeText ?? "",
                gapMark: content.gapMark ?? "___",
                instruction: content.instruction ?? "",
                userFeedback: BLANK_FEEDBACK,
                gaps: (content.gaps ?? []).map((gap) => ({
                  options: gap.options ?? [],
                  correctAnswer: "",
                })),
              },
            ]}
            onValidate={async (answers) => {
              await save(JSON.stringify(answers));
              return false;
            }}
          />
        );
      case "CLOZE_FREETEXT":
        return (
          <QuizGapwordFreeText
            {...collect}
            entries={[
              {
                clozeText: content.clozeText ?? "",
                gapMark: content.gapMark ?? "___",
                subType: "CLOZE_FREETEXT",
                contentType: "QUIZ",
                instruction: content.instruction ?? "",
                userFeedback: BLANK_FEEDBACK,
                gaps: (content.gaps ?? []).map(() => ({
                  referenceAnswer: [],
                })),
              },
            ]}
            onValidate={async (answers) => {
              await save(encodeClozeFreetextAnswers(answers));
              return storedWithoutVerdict();
            }}
          />
        );
      case "MATCHING_DEFINITION":
      case "MATCHING_SENTENCE":
        return (
          <QuizMatching
            {...collect}
            entries={[
              {
                subType: content.subType,
                contentType: "QUIZ",
                instruction: content.instruction ?? "",
                userFeedback: BLANK_FEEDBACK,
                pairs: (content.firstParts ?? []).map((firstPart, index) => ({
                  firstPart,
                  secondPart: content.secondParts?.[index] ?? "",
                })),
              },
            ]}
            onValidate={async (pairs) => {
              await save(JSON.stringify(pairs));
              return false;
            }}
          />
        );
      case "FREETEXT":
        return (
          <QuizFreetext
            {...collect}
            data={{
              instruction: content.instruction ?? "",
              contentType: "QUIZ",
            }}
            onValidate={async (answer) => {
              await save(answer);
              return storedWithoutVerdict();
            }}
          />
        );
      case "CODE_FREETEXT":
        return (
          <QuizCodeFreetext
            {...collect}
            entries={[
              {
                challenge: content.challenge ?? content.codeChallenge,
                codeChallenge: content.codeChallenge,
                subtasks: content.subtasks ?? [],
                explanations: [],
              },
            ]}
            onValidate={async (code) => {
              await save(code);
              return storedWithoutVerdict();
            }}
          />
        );
      case "CODE_DEBUG":
        return (
          <QuizCodeDebug
            {...collect}
            entries={[
              {
                challenge: content.challenge ?? content.debugChallenge,
                codeWithBugs: content.codeWithBugs ?? "",
                errorMessage: content.errorMessage ?? "",
                explanation: "",
              },
            ]}
            onValidate={async (code) => {
              await save(code);
              return storedWithoutVerdict();
            }}
          />
        );
      case "CODE_ASSERTION":
        return (
          <QuizCodeAssertion
            {...collect}
            entries={[
              {
                challenge: content.challenge ?? "",
                codeStub: content.codeStub ?? "",
                testCode: "",
                solution: "",
                implementationGoal: content.implementationGoal,
              },
            ]}
            onValidate={async (code) => {
              await save(code);
              return storedWithoutVerdict();
            }}
          />
        );
      case "CODE_EXPLANATION":
        return (
          <QuizCodeExplanation
            {...collect}
            entries={[
              {
                challenge: content.challenge ?? "",
                codeSnippet: content.codeSnippet ?? "",
                explanation: "",
              },
            ]}
            onValidate={async (answer) => {
              await save(answer);
              return storedWithoutVerdict();
            }}
          />
        );
      default:
        return null;
    }
  };

  const questionBody = renderQuestion();

  if (isWideQuiz(content.subType)) {
    return <div className="h-full min-h-0">{questionBody}</div>;
  }

  return (
    <InteractiveElementInlineCard
      title={cardTitle(content)}
      illustrationKind={illustrationFor(content.subType)}
    >
      {questionBody}
    </InteractiveElementInlineCard>
  );
};

export { QuizRunQuestion };
